import React, {useState} from 'react';

import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {BannerReact} from 'common/components/banner';
import {BannerAction, BannerTheme, CallToAction} from 'common/components/banner/types';
import {AccountPlanType} from 'common/report/queries/app-api/project-metadata-client';
import {NO_ICON} from 'common/components/banner/helpers';

import {ReplayBannerType} from './util';

const UTM_SOURCE_SR_GATE = `utm_source=session-replay-gate`;
export interface ReplayBannerProps {
  bannerType: ReplayBannerType;
}

type BannerMessagingType = Partial<{
  [replayBannerKey in ReplayBannerType]: {
    headerText: string;
    bodyText: Partial<{
      [accountPlanKey in AccountPlanType]: string;
    }>;
  };
}>;

const BannerMessaging: BannerMessagingType = {
  [ReplayBannerType.Dismissible]: {
    headerText: `You've used 85% of your session replay volume for this month.`,
    bodyText: {
      [AccountPlanType.Free]: `Your access to session replays will be limited when your volume is reached. Increase your volume by upgrading to the Growth plan.`,
      [AccountPlanType.Growth]: `Your access to session replays will be limited when your volume is reached. Increase your volume by upgrading to the Enterprise plan.`,
      [AccountPlanType.Enterprise]: `Your access to session replays will be limited when your volume is reached. To increase your session replay volume, talk to sales.`,
    },
  },
  [ReplayBannerType.NonDismissible]: {
    headerText: `More insights are waiting for you`,
    bodyText: {
      [AccountPlanType.Free]: `Unlock up to 20,000 session replays every month when you upgrade to the Growth plan.`,
      [AccountPlanType.Growth]: `Purchase custom amounts of session replays by upgrading to the Enterprise plan.`,
      [AccountPlanType.Enterprise]: `To increase your session replay volume, talk to sales.`,
    },
  },
};

function getUpgradeCtaByAccountType(
  accountType: AccountPlanType,
  orgId: number | undefined,
  bannerType: ReplayBannerType,
): CallToAction {
  let cta: CallToAction = {
    actionName: BannerAction.Upgrade,
    trackEventName: `Session Replay Upsell Banner Clicked`,
    trackProps: {
      account_type: accountType,
      banner_type: bannerType,
    },
    icon: `brand-upgrade`,
    text: `Upgrade Plan`,
    href: `/pricing/plan-builder/?org=${orgId}&${UTM_SOURCE_SR_GATE}`,
  };
  if (accountType === AccountPlanType.Enterprise || accountType === AccountPlanType.Growth) {
    cta = {
      ...cta,
      text: `Talk to Sales`,
      href: `/contact-us/sales/?${UTM_SOURCE_SR_GATE}`,
    };
  }
  return cta;
}

export function ReplayBanner(props: ReplayBannerProps) {
  const currentProject = useCurrentProject();
  const orgId = currentProject.get().value?.organizationId;
  const accountType = currentProject.get().value?.getAccountPlanType() || AccountPlanType.Unknown;

  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(true);
  if (!isBannerVisible || props.bannerType === ReplayBannerType.NoBanner) {
    return null;
  }
  const _ctas: CallToAction[] = [getUpgradeCtaByAccountType(accountType, orgId, props.bannerType)];
  if (props.bannerType === ReplayBannerType.Dismissible) {
    _ctas.push({
      actionName: BannerAction.Dismiss,
      trackEventName: `Session Replay Upsell Banner Dismissed`,
      trackProps: {
        account_type: accountType,
        banner_type: props.bannerType,
      },
      text: `Dismiss`,
    });
  }
  return (
    <BannerReact
      theme={BannerTheme.Info}
      headerText={BannerMessaging[props.bannerType]?.headerText}
      bodyText={BannerMessaging[props.bannerType]?.bodyText[accountType]}
      ctas={_ctas}
      icon={NO_ICON}
      onAction={(ev) => {
        if (ev.detail.actionName === BannerAction.Dismiss) {
          setIsBannerVisible(false);
        }
      }}
    />
  );
}
